import React, { useState } from "react";
import Icon from "../../../components/AppIcon";

const SkillRelationshipMap = ({ activeSkill }) => {
  const [hoveredConnection, setHoveredConnection] = useState(null);

  const skillConnections = {
    React: {
      primary: ["JavaScript", "HTML", "CSS"],
      secondary: ["TypeScript", "Node.js", "Webpack"],
      tools: ["Vite", "ESLint", "Prettier"],
      testing: [
        "Jest",
        //  'React Testing Library'
      ],
      state: ["Redux", "Context API", "Zustand"],
    },
    "Node.js": {
      primary: ["JavaScript", "Express", "NPM"],
      secondary: ["TypeScript", "MongoDB", "PostgreSQL"],
      tools: ["Docker", "PM2", "Nodemon"],
      testing: ["Jest", "Supertest", "Mocha"],
      cloud: ["AWS", "Heroku", "Vercel"],
    },
    Python: {
      primary: ["Django", "Flask", "FastAPI"],
      secondary: ["PostgreSQL", "Redis", "Celery"],
      tools: ["Docker", "Poetry", "Black"],
      testing: ["Pytest", "Unittest", "Coverage"],
      ai: ["TensorFlow", "PyTorch", "Scikit-learn"],
    },
    AWS: {
      primary: ["EC2", "S3", "RDS"],
      secondary: ["Lambda", "CloudFormation", "VPC"],
      tools: ["CLI", "CDK", "Terraform"],
      monitoring: ["CloudWatch", "X-Ray", "Config"],
      security: ["IAM", "KMS", "Secrets Manager"],
    },
  };

  const getConnectionData = () => {
    if (!activeSkill || !skillConnections?.[activeSkill?.name]) {
      return skillConnections?.["React"]; // Default fallback
    }
    return skillConnections?.[activeSkill?.name];
  };

  const connections = getConnectionData();
  const skillName = activeSkill?.name || "React";

  const connectionCategories = [
    {
      key: "primary",
      name: "Core Dependencies",
      color: "text-conversion-accent",
      bg: "bg-conversion-accent/10",
    },
    {
      key: "secondary",
      name: "Related Technologies",
      color: "text-trust-builder",
      bg: "bg-trust-builder/10",
    },
    {
      key: "tools",
      name: "Development Tools",
      color: "text-warning",
      bg: "bg-warning/10",
    },
    {
      key: "testing",
      name: "Testing Framework",
      color: "text-success",
      bg: "bg-success/10",
    },
    {
      key: "state",
      name: "State Management",
      color: "text-purple-400",
      bg: "bg-purple-400/10",
    },
    {
      key: "cloud",
      name: "Cloud Services",
      color: "text-blue-400",
      bg: "bg-blue-400/10",
    },
    {
      key: "ai",
      name: "AI/ML Libraries",
      color: "text-pink-400",
      bg: "bg-pink-400/10",
    },
    {
      key: "monitoring",
      name: "Monitoring",
      color: "text-orange-400",
      bg: "bg-orange-400/10",
    },
    {
      key: "security",
      name: "Security",
      color: "text-red-400",
      bg: "bg-red-400/10",
    },
  ];

  return (
    <div className="bg-card border border-border rounded-xl p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-semibold text-foreground mb-2">
            Technology Relationships
          </h3>
          <p className="text-muted-foreground">
            How{" "}
            <span className="text-conversion-accent font-medium">
              {skillName}
            </span>{" "}
            connects with other technologies
          </p>
        </div>
        <div className="w-12 h-12 bg-conversion-accent/10 rounded-lg flex items-center justify-center">
          <Icon name="Network" size={24} className="text-conversion-accent" />
        </div>
      </div>
      {/* Central Skill Node */}
      <div className="relative">
        <div className="flex justify-center mb-8">
          <div className="relative">
            <div className="w-20 h-20 bg-gradient-to-br from-conversion-accent to-trust-builder rounded-full flex items-center justify-center shadow-elevation">
              <Icon
                name={activeSkill?.icon || "Code"}
                size={32}
                className="text-white"
              />
            </div>
            <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2">
              <div className="bg-card border border-border rounded-lg px-3 py-1">
                <span className="text-sm font-medium text-foreground">
                  {skillName}
                </span>
              </div>
            </div>

            {/* Pulse Animation */}
            <div className="absolute inset-0 rounded-full bg-conversion-accent/20 animate-ping" />
          </div>
        </div>

        {/* Connection Categories */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {connectionCategories?.map((category) => {
            const categoryConnections = connections?.[category?.key];
            if (!categoryConnections || categoryConnections?.length === 0)
              return null;

            return (
              <div
                key={category?.key}
                className={`relative p-4 rounded-lg border transition-smooth cursor-pointer ${
                  hoveredConnection === category?.key
                    ? `border-conversion-accent/50 ${category?.bg}`
                    : "border-border hover:border-muted-foreground/30"
                }`}
                onMouseEnter={() => setHoveredConnection(category?.key)}
                onMouseLeave={() => setHoveredConnection(null)}
              >
                {/* Category Header */}
                <div className="flex items-center space-x-2 mb-3">
                  <div
                    className={`w-3 h-3 rounded-full ${category?.color?.replace(
                      "text-",
                      "bg-"
                    )}`}
                  />
                  <h4 className={`text-sm font-medium ${category?.color}`}>
                    {category?.name}
                  </h4>
                </div>
                {/* Connected Technologies */}
                <div className="space-y-2">
                  {categoryConnections?.map((tech, index) => (
                    <div
                      key={index}
                      className={`flex items-center space-x-2 p-2 rounded-md transition-smooth ${
                        hoveredConnection === category?.key
                          ? "bg-muted/20"
                          : "hover:bg-muted/10"
                      }`}
                    >
                      <div
                        className={`w-2 h-2 rounded-full ${category?.color?.replace(
                          "text-",
                          "bg-"
                        )}`}
                      />
                      <span className="text-sm text-foreground">{tech}</span>
                    </div>
                  ))}
                </div>
                {/* Connection Line (Visual Effect) */}
                {hoveredConnection === category?.key && (
                  <div className="absolute top-0 left-1/2 w-0.5 h-4 bg-conversion-accent transform -translate-x-1/2 -translate-y-4" />
                )}
              </div>
            );
          })}
        </div>

        {/* Relationship Insights */}
        <div className="mt-8 bg-muted/10 rounded-lg p-6">
          <div className="flex items-center space-x-2 mb-4">
            <Icon
              name="Lightbulb"
              size={20}
              className="text-conversion-accent"
            />
            <h4 className="text-lg font-semibold text-foreground">
              Technology Insights
            </h4>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h5 className="text-sm font-medium text-foreground mb-2">
                Best Practices
              </h5>
              <ul className="space-y-1 text-sm text-muted-foreground">
                <li className="flex items-center space-x-2">
                  <Icon
                    name="CheckCircle2"
                    size={12}
                    className="text-success"
                  />
                  <span>Always pair with TypeScript for large projects</span>
                </li>
                <li className="flex items-center space-x-2">
                  <Icon
                    name="CheckCircle2"
                    size={12}
                    className="text-success"
                  />
                  <span>Use testing frameworks for reliable code</span>
                </li>
                <li className="flex items-center space-x-2">
                  <Icon
                    name="CheckCircle2"
                    size={12}
                    className="text-success"
                  />
                  <span>Implement proper state management patterns</span>
                </li>
              </ul>
            </div>

            <div>
              <h5 className="text-sm font-medium text-foreground mb-2">
                Common Patterns
              </h5>
              <ul className="space-y-1 text-sm text-muted-foreground">
                <li className="flex items-center space-x-2">
                  <Icon
                    name="ArrowRight"
                    size={12}
                    className="text-trust-builder"
                  />
                  <span>Component-based architecture</span>
                </li>
                <li className="flex items-center space-x-2">
                  <Icon
                    name="ArrowRight"
                    size={12}
                    className="text-trust-builder"
                  />
                  <span>Unidirectional data flow</span>
                </li>
                <li className="flex items-center space-x-2">
                  <Icon
                    name="ArrowRight"
                    size={12}
                    className="text-trust-builder"
                  />
                  <span>Declarative programming style</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SkillRelationshipMap;
